import { Injectable } from '@angular/core';
import { LoggerOptions } from '../logger-options';
import { LogMethod } from '../log-levels';

export interface LoggerProvider {
  debug(template: string, ...optionalParams: unknown[]): void;
  info(template: string, ...optionalParams: unknown[]): void;
  warn(template: string, ...optionalParams: unknown[]): void;
  error(template: string, ...optionalParams: unknown[]): void;
  table(data: unknown, columns?: string[]): void;
  setOptions(options: LoggerOptions): void;
}

@Injectable()
export class ConsoleColorProvider implements LoggerProvider {
  private options!: LoggerOptions;

  setOptions(options: LoggerOptions): void {
    this.options = options;
  }

  debug(template: string, ...optionalParams: unknown[]): void {
    this.logWithColor(LogMethod.DEBUG, '#800080', template, ...optionalParams);
  }

  info(template: string, ...optionalParams: unknown[]): void {
    this.logWithColor(LogMethod.INFO, '#6495ED', template, ...optionalParams);
  }

  warn(template: string, ...optionalParams: unknown[]): void {
    this.logWithColor(LogMethod.WARN, '#FFA500', template, ...optionalParams);
  }

  error(template: string, ...optionalParams: unknown[]): void {
    this.logWithColor(LogMethod.ERROR, '#FF0000', template, ...optionalParams);
  }

  table(data: unknown, columns?: string[]): void {
    console.table(data, columns);
  }

  private logWithColor(method: LogMethod, color: string, template: string, ...optionalParams: unknown[]): void {
    console[method](`%c${template}`, `color: ${color}`, ...optionalParams);
  }
}

@Injectable()
export class AdvancedConsoleProvider implements LoggerProvider {
  private options!: LoggerOptions;

  setOptions(options: LoggerOptions): void {
    this.options = options;
  }

  debug(template: string, ...optionalParams: unknown[]): void {
    this.log(LogMethod.DEBUG, template, '#800080', ...optionalParams);
  }

  info(template: string, ...optionalParams: unknown[]): void {
    this.log(LogMethod.INFO, template, '#6495ED', ...optionalParams);
  }

  warn(template: string, ...optionalParams: unknown[]): void {
    this.log(LogMethod.WARN, template, '#FFA500', ...optionalParams);
  }

  error(template: string, ...optionalParams: unknown[]): void {
    this.log(LogMethod.ERROR, template, '#FF0000', ...optionalParams);
  }

  table(data: unknown, columns?: string[]): void {
    this.logTable(data, columns);
  }

  private log(method: LogMethod, template: string, color: string, ...optionalParams: unknown[]): void {
    if (this.options.collapsed) {
      console.groupCollapsed(`%c${template}`, `color: ${color}`);
      console[method](...optionalParams);
      this.trace();
      console.groupEnd();
    } else {
      console[method](`%c${template}`, `color: ${color}`, ...optionalParams);
      this.trace();
    }
  }

  private trace(): void {
    if (this.options.enableTrace) {
      if (this.options.traceCollapsed) {
        console.groupCollapsed('trace...');
      }
      console.trace();
      if (this.options.traceCollapsed) {
        console.groupEnd();
      }
    }
  }

  private logTable(data: unknown, columns?: string[]): void {
    if (this.options.tableCollapsed) {
      console.groupCollapsed('table...');
    }
    console.table(data, columns);
    if (this.options.tableCollapsed) {
      console.groupEnd();
    }
  }
}
